// 각성: 모으기 → 섬광 → 컷인 → 난무 → 복귀(COMBAT-FEEL 5장). 영웅 시간으로 진행하고, 세계 배율은 단계표를 따른다.
import { AWAKEN, FIXED_DT, HERO, heroAwaken } from "../shared/config";
import { Ev } from "../shared/events";
import { AwakenPhase, HeroAct, MobState, type AwakenPhaseId } from "../shared/types";
import { HitSource } from "./combat";
import type { GameSim } from "./game";

/** 모으기 끌어당김 속도(m/s, COMBAT-FEEL 5장) */
const PULL_SPEED = AWAKEN.pullSpeed;
/** 난무 타격 간격(틱): 영웅별 타격 수로 난무 길이를 나눈다(카엘 6타 = 9틱, 라이라 폭풍 화살비 8번 = 6틱) */
const intervalOf = (hits: number): number => Math.floor(AWAKEN.barrage / hits);

export class Awakening {
  private skip = false;
  constructor(private readonly g: GameSim) {}

  /** 현재 몹·보스 시간 배율. 복귀는 recoverRampTicks 동안 시작값에서 1로 선형 회복 */
  worldScale(): number {
    const a = this.g.awaken;
    if (a.phase === AwakenPhase.Recover) {
      const from = AWAKEN.worldScale[AwakenPhase.Recover];
      return Math.min(1, from + ((1 - from) * a.phaseTick) / AWAKEN.recoverRampTicks);
    }
    return AWAKEN.worldScale[a.phase];
  }

  start(): boolean {
    const g = this.g, a = g.awaken, h = g.hero;
    if (h.awakenGauge < 1 || a.phase !== AwakenPhase.None) return false;
    g.clearJustDodgeCounter();
    h.awakenGauge = 0; h.act = HeroAct.Awaken; h.actTick = 0; h.invulnerable = true;
    a.usedThisRun++; a.slashIndex = 0;
    this.skip = g.settings.skipCutin && a.usedThisRun > 1;
    g.combat.beginAction(HitSource.Awaken);
    this.setPhase(AwakenPhase.Gather);
    return true;
  }

  /** 판 시작: 진행 중 각성과 사용 횟수까지 지운다 */
  reset(): void {
    this.cancel();
    this.g.awaken.usedThisRun = 0;
  }

  /** 결과·패배 전환 시 진행 중 연출만 끊는다(사용 횟수 유지) */
  cancel(): void {
    const a = this.g.awaken;
    a.phase = AwakenPhase.None; a.phaseTick = 0; a.phaseTotal = 0; a.slashIndex = 0;
    this.skip = false;
  }

  private length(phase: AwakenPhaseId): number {
    switch (phase) {
      case AwakenPhase.Gather: return AWAKEN.gather;
      case AwakenPhase.Flash: return AWAKEN.flash;
      case AwakenPhase.Cutin: return this.skip ? 0 : AWAKEN.cutin;
      case AwakenPhase.Barrage: return AWAKEN.barrage;
      case AwakenPhase.Recover: return AWAKEN.recover;
      default: return 0;
    }
  }

  private setPhase(phase: AwakenPhaseId): void {
    const a = this.g.awaken;
    a.phase = phase; a.phaseTick = 0; a.phaseTotal = this.length(phase);
    this.g.emit(Ev.AwakenPhase).a = phase;
  }

  /** 영웅 step 안에서 매 틱 호출. first는 시작 틱(단계 틱 0) */
  step(first = false): void {
    const g = this.g, a = g.awaken, h = g.hero;
    if (a.phase === AwakenPhase.None) return;
    if (!first) { a.phaseTick++; h.actTick++; }
    if (a.phaseTick >= a.phaseTotal) {
      if (a.phase === AwakenPhase.Recover) {
        this.setPhase(AwakenPhase.None);
        h.act = HeroAct.Idle; h.actTick = 0; h.invulnerable = false;
        return;
      }
      this.setPhase((a.phase + 1) as AwakenPhaseId);
      // 컷인 생략: 길이 0 단계는 이벤트만 남기고 바로 난무로 넘어간다.
      if (a.phase === AwakenPhase.Cutin && a.phaseTotal === 0) this.setPhase(AwakenPhase.Barrage);
    }
    h.invulnerable = true;
    if (a.phase === AwakenPhase.Gather) this.pull();
    else if (a.phase === AwakenPhase.Barrage) {
      const def = heroAwaken(g.heroId);
      if (a.phaseTick % intervalOf(def.hits) === 0 && a.slashIndex < def.hits) this.slash();
    }
  }

  /** 모으기: 반경 안 몹을 영웅 쪽으로 끌어당긴다(각성 타임라인 = 실시간) */
  private pull(): void {
    const g = this.g, m = g.mobs, h = g.hero;
    for (let i = 0; i < m.capacity; i++) {
      if (m.state[i] === MobState.Inactive || m.state[i] === MobState.Dying) continue;
      const radius = g.mobController.radius(i);
      const dx = h.x - m.x[i], dz = h.z - m.z[i], distance = Math.hypot(dx, dz);
      if (distance > AWAKEN.pullRadius || distance <= HERO.radius + radius) continue;
      const move = Math.min(distance - HERO.radius - radius, PULL_SPEED * FIXED_DT);
      g.moveMob(i, (dx / distance) * move, (dz / distance) * move, radius);
    }
  }

  /**
   * 난무 한 번. 카엘 = 진홍 초승달 난무(참격 6), 라이라 = 폭풍 화살비(넓은 원형 다단 화살비 8번, c022). 판정은 같은 영웅 중심 원.
   * Ev.AwakenSlash a = 번호, b = 각도(라이라 렌더는 화살비 낙하 번호로 쓴다: lyra_awaken_rain)
   */
  private slash(): void {
    const g = this.g, a = g.awaken, h = g.hero, def = heroAwaken(g.heroId);
    const e = g.emit(Ev.AwakenSlash);
    e.x = h.x; e.z = h.z; e.a = a.slashIndex; e.b = h.facing + (a.slashIndex * Math.PI) / 3;
    g.combat.newPulse();
    g.combat.setMultiHit(a.slashIndex < def.hits - 1);
    a.slashIndex++;
    // 난무 타격은 히트스톱을 만들지 않는다(AWAKEN.hitstop). 2.4초 타임라인을 고정한다.
    // 각성 공명(캡스톤): 각성 피해 배율
    g.combat.circle(def.radius, def.mult * g.gmods.awakenDamageMult, true, AWAKEN.hitstop);
  }
}
